import { AuthConditionType, AuthorisationSummary, AuthType, SourceLocationType } from '@types';

import { DisplayController } from './DisplayController';

// ---->>> On changes in file: COPY-PASTE FROM THIS LINE <<<----
/**
 * It is advised that all code lines below (excluding imports), should remain consistent across all
 * client-side repositories or projects (website, mobile) due to the inversion of dependencies.
 * Consequently, it is recommended to copy and paste all code, from this line to the end of the file,
 * to apply same changes on all repositories or projects.
 */

// Authorisation types
const LTW = 'LTW';
const NRL = 'NRL';
const SWAN = 'SWAN';
const PTW = 'PTW';
const LTI = 'LTI';
const IWA = 'IWA';
const ROL = 'ROL';

/**
 * Display of authorization data details
 */
interface AuthorisationDisplay {
	// Main
	type: string;
	status: string;
	// Details
	reference: string;
	expiryDate: string;
	waterPlan: string;
	managementGroupArea: string;
	managementSubgroup: string;
	location: string;
	place: string;
	source: string;
	sourceLocation: string[];
	additionalDescriptions: string[];
	attachedLands: string[];
	nominalEntitlement: string;
	nominalVolume: string;
	maxVolume: string;
	waterAllocationGroup: string;
	maxAreaHa: string;
	volumetricLimits: string[];
	purpose: string;
	maximumRates: string;
	priority: string;
	capacity: string;
	office: string;
	allocation: string;
	// Conditions
	conditions: string[];
	flowCondition: string[];
	otherCondition: string[];
}

type AnyAuthorisation = AuthType | AuthorisationSummary;

export class AuthorisationDisplayController extends DisplayController<
	AnyAuthorisation,
	AuthorisationDisplay
> {
	/**
	 * Transformed type code of Authorisation
	 */
	private typeCode: string;

	constructor(data: AnyAuthorisation) {
		super(data);
		this.typeCode = this.transformAuthTypeCode((data as AuthType)?.typeCode); // initialize typeCode first
		this.visible = this.visibilityRules(data as AuthType); // needed after setting typeCode
		this.values = this.transformData(data as AuthType); // needed after setting typeCode
	}

	/**
	 * Initialize authorisation type code pushing back any derived LTI code to LTI
	 * Example: 'LTIIEW' is overwriten as 'LTI'
	 * @param {string} authorisation type code
	 * @returns {string} transformed authorisation. Empty string by default
	 */
	private transformAuthTypeCode(authTypeCode?: string) {
		if (!authTypeCode) {
			return '';
		}
		if (authTypeCode?.startsWith(LTI)) {
			return LTI;
		}
		return authTypeCode;
	}

	protected getLabels() {
		return {
			type: 'Type',
			status: 'Status',
			reference: 'Reference',
			expiryDate: 'Expiry',
			waterPlan: 'Water plan',
			managementGroupArea: 'Water management area',
			managementSubgroup: 'Subgroup',
			location: 'Location',
			place: 'Place',
			source: 'Source of water',
			sourceLocation: 'Location of water details',
			additionalDescriptions: 'Location of water additional description',
			attachedLands: 'Attached lands',
			nominalEntitlement: 'Nominal entitlement',
			nominalVolume: 'Nominal volume',
			maxVolume: 'Maximum volume',
			maxAreaHa: 'Maximum area',
			volumetricLimits: 'Volumetric limits',
			maximumRates: 'Maximum rate',
			purpose: 'Purpose',
			capacity: 'Capacity',
			priority: 'Priority',
			waterAllocationGroup: 'Water allocation group',
			office: 'Office',
			allocation: 'Announced allocation',
			conditions: '',
			flowCondition: 'Flow conditions',
			otherCondition: 'Other conditions',
		};
	}

	/**
	 * Define display rules based on Authorisation Type and other conditions
	 * @param data
	 * @returns
	 */
	protected visibilityRules(data: AuthType) {
		return {
			type: true,
			status: true,
			reference: !this.isEmptyValue(data.reference),
			expiryDate:
				[LTW, SWAN, PTW, LTI].includes(this.typeCode) && !this.isEmptyDate(data.expiryDate),
			waterPlan: !this.isEmptyValue(data.waterPlan),
			managementGroupArea: !this.isEmptyValue(data.managementGroupArea),
			managementSubgroup: !this.isEmptyValue(data.managementSubgroup),
			location: [LTW, NRL, SWAN, ROL].includes(this.typeCode) && !this.isEmptyValue(data.location),
			place: [NRL, ROL].includes(this.typeCode) && !this.isEmptyValue(data.place),
			source: [LTW, SWAN, PTW, LTI, IWA].includes(this.typeCode) && !this.isEmptyValue(data.source),
			sourceLocation:
				[LTW, SWAN, PTW, LTI, IWA].includes(this.typeCode) &&
				!this.isEmptyValue(data.sourceLocation),
			additionalDescriptions:
				[LTW].includes(this.typeCode) &&
				(data.additionalDescriptions ?? []).some(
					item => !this.isEmptyValue(item.waterAdditionalDescription),
				),
			attachedLands: [LTW, IWA].includes(this.typeCode) && !this.isEmptyValue(data.attachedLands),
			nominalEntitlement:
				[LTW, IWA].includes(this.typeCode) &&
				data.volume != null &&
				Number(this.formatNumber(data.volume.toString(), 3)) > 0,
			nominalVolume: [NRL, ROL].includes(this.typeCode) && data.volume > 0,
			maxVolume: [PTW, SWAN].includes(this.typeCode) && data.maxVolumeToBeTaken > 0,
			maxAreaHa:
				[LTW].includes(this.typeCode) &&
				!this.isEmptyValue(data.maxAreaHa) &&
				Number(this.formatNumber(data.maxAreaHa, 3)) > 0,
			volumetricLimits:
				[NRL].includes(this.typeCode) &&
				(data.volumetricLimits ?? []).some(
					item =>
						!this.isEmptyValue(item.volumetricLimit) &&
						Number(this.formatNumber(item.volumetricLimit, 3)) > 0,
				),
			maximumRates:
				[LTW, NRL, SWAN, PTW].includes(this.typeCode) &&
				!this.isEmptyValue(data.maximumRates) &&
				Number(this.formatNumber(data.maximumRates, 3)) > 0,
			purpose: !this.isEmptyValue(data.purpose),
			capacity:
				[LTI].includes(this.typeCode) &&
				(data.typeCode == 'LTIIEX'
					? !this.isEmptyValue(data.capacityOfExcavation) &&
						Number(this.formatNumber(data.capacityOfExcavation, 2)) > 0
					: !this.isEmptyValue(data.capacity) && Number(this.formatNumber(data.capacity, 2)) > 0),
			priority:
				[LTW, NRL, IWA, ROL, LTI].includes(this.typeCode) && !this.isEmptyValue(data.priority),
			waterAllocationGroup:
				[NRL].includes(this.typeCode) && !this.isEmptyValue(data.waterAllocationGroup),
			office: !this.isEmptyValue(data.office),
			allocation:
				!this.isEmptyValue(data.allocation) &&
				Number(this.formatNumber(data.allocation ?? '', 0)) > 0,
			conditions: !this.isEmptyValue(
				this.typeCode == NRL ? this.authNRLConditions(data?.conditions) : data.conditions,
			),
			flowCondition:
				this.typeCode == NRL && !this.isEmptyValue(this.authNRLFlowConditions(data.conditions)),
			otherCondition:
				[LTW, SWAN, PTW, LTI, IWA].includes(this.typeCode) &&
				!this.isEmptyValue(data.otherCondition),
		};
	}

	protected transformData(data: AuthType) {
		const defaultStr = ' - ';

		return {
			type: !this.isEmptyValue(data.authorisationType)
				? this.toSentenceCase(data.authorisationType)
				: defaultStr,
			status: !this.isEmptyValue(data.status) ? data.status : defaultStr,
			reference: !this.isEmptyValue(data.reference) ? data.reference : defaultStr,
			expiryDate: !this.isEmptyDate(data.expiryDate)
				? this.convertTimeToDisplayFormat(data.expiryDate)
				: defaultStr,
			waterPlan: !this.isEmptyValue(data.waterPlan) ? data.waterPlan : defaultStr,
			managementGroupArea: !this.isEmptyValue(data.managementGroupArea)
				? data.managementGroupArea
				: defaultStr,
			managementSubgroup: !this.isEmptyValue(data.managementSubgroup)
				? data.managementSubgroup
				: defaultStr,
			location: !this.isEmptyValue(data.location) ? data.location : defaultStr,
			place: !this.isEmptyValue(data.place) ? data.place : defaultStr,
			source: !this.isEmptyValue(data.source) ? data.source : defaultStr,
			sourceLocation: !this.isEmptyValue(data.sourceLocation)
				? data.sourceLocation.map((location: SourceLocationType) => {
						let mappedValue = '';
						if (location.lot?.length || location.plan?.length) {
							mappedValue = `${location.lot ? location.lot : '-'}/${
								location.plan ? location.plan : '-'
							}`;
						}
						if (location.land && location.land != mappedValue) {
							mappedValue += ` ${location.land}`;
						}
						return mappedValue;
					})
				: [],
			additionalDescriptions: (data.additionalDescriptions ?? []).map(
				item => item.waterAdditionalDescription,
			),
			attachedLands: (data.attachedLands ?? []).map(item => item.attachedLand),
			nominalEntitlement: Number.isFinite(data.volume)
				? `${this.formatNumber(data.volume.toString(), 3)} ML`
				: defaultStr,
			nominalVolume: Number.isFinite(data.volume)
				? `${this.formatNumber(data.volume.toString(), 3)} ML`
				: defaultStr,
			maxVolume: Number.isFinite(data.maxVolumeToBeTaken)
				? `${this.formatNumber(data.maxVolumeToBeTaken.toString(), 3)} ML`
				: defaultStr,
			maxAreaHa: data.maxAreaHa ? `${this.formatNumber(data.maxAreaHa, 3)} HA` : defaultStr,
			volumetricLimits: (data.volumetricLimits ?? [])
				.filter(item => !this.isEmptyValue(item.volumetricLimit))
				.map(item => this.formatNumberWithUnit(item.volumetricLimit, 3)),
			maximumRates: !this.isEmptyValue(data.maximumRates)
				? this.formatNumberWithUnit(data.maximumRates, 3)
				: defaultStr,
			purpose: !this.isEmptyValue(data.purpose) ? data.purpose : defaultStr,
			capacity: this.transformCapacity(data, defaultStr),
			priority: !this.isEmptyValue(data.priority) ? data.priority : defaultStr,
			waterAllocationGroup: !this.isEmptyValue(data.waterAllocationGroup)
				? data.waterAllocationGroup
				: defaultStr,
			office: !this.isEmptyValue(data.office) ? data.office : defaultStr,
			allocation:
				data.allocation && Number(this.formatNumber(data.allocation, 0)) > 0
					? `${this.formatNumber(data.allocation, 0)}%`
					: defaultStr,
			conditions: (
				(this.typeCode == NRL ? this.authNRLConditions(data?.conditions) : data.conditions) ?? []
			).map(item => this.formatAuthorisationCondition(item)),
			flowCondition: this.authNRLFlowConditions(data.conditions).map(item =>
				this.formatAuthorisationCondition(item),
			),
			otherCondition: (data.otherCondition ?? '')
				.split('\r\n')
				.filter(item => item.trim().length > 0),
		};
	}

	private transformCapacity(data: AuthType, defaultStr: string) {
		if (data.typeCode == 'LTIIEX') {
			if (!this.isEmptyValue(data.capacityOfExcavation)) {
				return this.formatNumber(data.capacityOfExcavation, 2);
			}
		} else if (this.typeCode == 'LTI') {
			if (!this.isEmptyValue(data.capacity)) {
				return this.formatNumber(data.capacity, 2);
			}
		}
		return defaultStr;
	}

	private formatAuthorisationCondition(condition: AuthConditionType) {
		return condition.reference + ' – ' + condition.result;
	}

	private authNRLConditions(conditions?: AuthConditionType[]) {
		return (conditions ?? []).filter(item => item.defType != 'ROP FLOW');
	}

	private authNRLFlowConditions(conditions?: AuthConditionType[]) {
		return (conditions ?? []).filter(item => item.defType == 'ROP FLOW');
	}
}
